import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useCurrentSpace } from "@/hooks/useCurrentSpace";
import { useAccountSpaces } from "@/hooks/useAccountSpaces.jsx";
import { useParams } from "react-router-dom";
import { Icon } from "@iconify/react";

export const SpaceSelector = ({ className, triggerClassName, contentClassName, onSpaceChange, size = "default" }) => {

    const { accountSpaces } = useAccountSpaces()
    const { space } = useCurrentSpace()
    const { accountId, spaceId } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const [searchQuery, setSearchQuery] = useState("")
    const [isOpen, setIsOpen] = useState(false)
    const searchInputRef = useRef(null)


    // Keep focus on search input when typing
    useEffect(() => {
        if (isOpen && searchInputRef.current) {
            const timeoutId = setTimeout(() => {
                searchInputRef.current?.focus()
            }, 0)
            return () => clearTimeout(timeoutId)
        }
    }, [searchQuery, isOpen])

    const handleSpaceChange = (newSpaceId) => {
        // Don't do anything if selecting the current space
        if (newSpaceId === spaceId) {
            return
        }

        // Call the onSpaceChange callback if provided
        if (onSpaceChange) {
            onSpaceChange(newSpaceId)
        }

        // Update URL path to include spaceId
        const newPath = location.pathname.replace(`/space/${spaceId}`, `/space/${newSpaceId}`)
        navigate(newPath)
    }


    // Determine trigger size classes
    const sizeClasses = {
        sm: "h-8 text-xs",
        default: "h-10 text-sm",
        lg: "h-12 text-base"
    }

    if (!accountSpaces || accountSpaces.length === 0) {
        return null // Don't render if no spaces available for this account
    }

    // Filter spaces based on search query - only show matching results
    const filteredSpaces = accountSpaces.filter((spaceItem) =>
        spaceItem.name.toLowerCase().includes(searchQuery.toLowerCase())
    )

    // Ensure current space is in the list for Select to work properly, but we'll hide it visually if it doesn't match
    const currentSpaceInFiltered = filteredSpaces.some(s => s._id === spaceId)
    const currentSpaceItem = accountSpaces.find(s => s._id === spaceId)

    return (
        <div className={cn("flex items-center w-full", className)}>
            <Select
                value={spaceId}
                onValueChange={handleSpaceChange}
                open={isOpen}
                onOpenChange={(open) => {
                    setIsOpen(open)
                    if (!open) {
                        setSearchQuery("")
                    }
                }}
            >
                <SelectTrigger
                    className={cn(
                        "flex items-center gap-2",
                        sizeClasses[size],
                        triggerClassName
                    )}
                >
                    <SelectValue placeholder={space?.name || "Select a space"} />
                </SelectTrigger>

                <SelectContent className={cn("", contentClassName)}>
                    <div
                        className="px-2 pb-2"
                        onPointerDown={(e) => e.stopPropagation()}
                        onPointerMove={(e) => e.stopPropagation()}
                        onPointerUp={(e) => e.stopPropagation()}
                    >
                        <Input
                            ref={searchInputRef}
                            placeholder="Search spaces..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onKeyDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                            onFocus={(e) => e.stopPropagation()}
                            className="h-8"
                            autoFocus
                        />
                    </div>
                    <SelectGroup>
                        {/* Hidden item for current space if not in filtered results - needed for Select to display value */}
                        {searchQuery && !currentSpaceInFiltered && currentSpaceItem && (
                            <SelectItem
                                key={currentSpaceItem._id}
                                value={currentSpaceItem._id}
                                className="hidden"
                            >
                                <span>{currentSpaceItem.name}</span>
                            </SelectItem>
                        )}
                        {
                            filteredSpaces.length > 0 ? (
                                filteredSpaces.map((spaceItem) => {
                                    return (
                                        <SelectItem
                                            key={spaceItem._id}
                                            value={spaceItem._id}
                                            className="cursor-pointer"
                                        >
                                            <div className="flex items-center gap-2">
                                                {/* <Icon icon="material-symbols-light:planet-outline-rounded" className="h-4 w-4 flex-shrink-0" /> */}
                                                <span className="truncate max-w-[140px]">{spaceItem.name} </span>
                                            </div>
                                        </SelectItem>
                                    )
                                })
                            ) : (
                                <div className="py-6 text-center text-sm text-muted-foreground">
                                    No spaces found
                                </div>
                            )
                        }
                    </SelectGroup>
                </SelectContent>
            </Select>
        </div>
    )
}

